'use client'

import { CalendarClock } from 'lucide-react'

import {
  AlertTone,
} from '@/components/alerts/WorkspaceAlert'

export type WorkspaceAlertItem = {
  id: string
  tone: AlertTone

  icon?: React.ReactNode

  title: string
  message: string

  ctaLabel?: string
  onCta?: () => void

  meta?: React.ReactNode
}

type Props = {
  visible?: boolean

  sellerMembershipActive?: boolean
  sellerMembershipExpiry?: string | null

  paymentUrl?: string | null

  hasStripeSubscription?: boolean
  stripeSubscriptionStatus?: string | null
  stripeCurrentPeriodEnd?: string | null

  thresholdDangerDays?: number
}

const DAY_MS =
  24 * 60 * 60 * 1000

function parseExpiryDate(
  raw?: string | null
): Date | null {
  if (!raw) return null

  const value =
    String(raw).trim()

  if (
    !value ||
    value === '0000-00-00'
  ) {
    return null
  }

  const dateOnlyMatch =
    value.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    )

  if (dateOnlyMatch) {
    const year =
      Number(dateOnlyMatch[1])

    const month =
      Number(dateOnlyMatch[2])

    const day =
      Number(dateOnlyMatch[3])

    return new Date(
      year,
      month - 1,
      day,
      23,
      59,
      59,
      999
    )
  }

  const parsed =
    new Date(value)

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return null
  }

  return parsed
}

function formatDate(
  raw?: string | null
) {
  const date =
    parseExpiryDate(raw)

  if (!date) return null

  try {
    return new Intl.DateTimeFormat(
      'es-MX',
      {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }
    ).format(date)
  } catch {
    return raw || null
  }
}

function getDaysLeft(
  expiry?: string | null
) {
  const expiryDate =
    parseExpiryDate(expiry)

  if (!expiryDate) return null

  const now =
    new Date()

  const diff =
    expiryDate.getTime() -
    now.getTime()

  return Math.ceil(
    diff / DAY_MS
  )
}

function isHealthyStripeStatus(
  status?: string | null
) {
  return [
    'active',
    'trialing',
  ].includes(
    String(status || '')
      .toLowerCase()
  )
}

function isProblemStripeStatus(
  status?: string | null
) {
  return [
    'past_due',
    'unpaid',
    'incomplete',
    'incomplete_expired',
    'canceled',
    'paused',
  ].includes(
    String(status || '')
      .toLowerCase()
  )
}

export function useBusinessMembershipExpiryAlert({
  visible = true,

  sellerMembershipActive = true,
  sellerMembershipExpiry = null,

  hasStripeSubscription = false,
  stripeSubscriptionStatus = null,

  thresholdDangerDays = 3,
}: Props): WorkspaceAlertItem | null {
  if (!visible) return null

  if (!sellerMembershipActive) {
    return null
  }

  const daysLeft =
    getDaysLeft(
      sellerMembershipExpiry
    )

  const expiryLabel =
    formatDate(
      sellerMembershipExpiry
    )

  if (daysLeft == null) {
    return null
  }

  if (daysLeft < 0) {
    return null
  }

  const stripeHealthy =
    hasStripeSubscription &&
    isHealthyStripeStatus(
      stripeSubscriptionStatus
    )

  const stripeProblem =
    hasStripeSubscription &&
    isProblemStripeStatus(
      stripeSubscriptionStatus
    )

  /*
  * Stripe activo o trialing:
  * la renovación es automática.
  * No mostramos ningún recordatorio.
  */
  if (stripeHealthy) {
    return null
  }

  /*
  * Si no existe un problema con Stripe,
  * sólo mostramos el recordatorio
  * cuando falten 3 días o menos.
  *
  * Esto cubre pagos manuales
  * o suscripciones externas.
  */
  if (
    !stripeProblem &&
    daysLeft > thresholdDangerDays
  ) {
    return null
  }

  const title =
    stripeProblem
      ? 'No pudimos procesar tu renovación automática'
      : daysLeft === 0
        ? 'Tu membresía vence hoy'
        : daysLeft === 1
          ? 'Tu membresía vence mañana'
          : `Tu membresía vence en ${daysLeft} días`

  const message =
    stripeProblem
      ? 'Tu comunidad sigue activa por ahora, pero necesitamos que actualices tu método de pago para evitar una pausa temporal.'
      : daysLeft === 0
        ? 'Renueva tu acceso hoy para evitar que tu comunidad se pause temporalmente.'
        : daysLeft === 1
          ? 'Renueva tu acceso antes de mañana para mantener activa la experiencia de tus miembros.'
          : 'Renueva tu acceso con anticipación para evitar que tu comunidad se pause temporalmente.'

  const ctaLabel =
    stripeProblem
      ? 'Actualizar pago'
      : 'Renovar ahora'

  /*
   * IMPORTANT:
   *
   * Cada estado tiene ID diferente.
   *
   * Si el usuario cierra el warning
   * de 7 días, el danger de 3 días
   * aparecerá igualmente después.
   */
  const id =
    stripeProblem
      ? 'business-membership-payment-problem'
      : 'business-membership-danger'

  return {
    id,

    tone: 'danger',

    icon: (
      <CalendarClock className="h-[18px] w-[18px]" />
    ),

    title,
    message,
    ctaLabel,

    onCta: () => {
      window.dispatchEvent(
        new CustomEvent(
          'hubee:business_membership_payment_open',
          {
            detail: {
              mode:
                stripeProblem
                  ? 'fix_payment'
                  : 'renew',
            },
          }
        )
      )
    },

    meta: (
      <>
        {expiryLabel ? (
          <>
            Vence:{' '}
            <span className="font-semibold tabular-nums text-foreground/80">
              {expiryLabel}
            </span>

            {' • '}
          </>
        ) : null}

        Tiempo restante:{' '}
        <span className="font-semibold tabular-nums text-foreground/80">
          {daysLeft === 0
            ? 'hoy'
            : daysLeft === 1
              ? '1 día'
              : `${daysLeft} días`}
        </span>
      </>
    ),
  }
}